'use client'

import { useRef, useEffect, useState } from 'react'
import { motion, useAnimationFrame, useMotionValue, useTransform, useSpring } from 'framer-motion'

type InfiniteSliderProps = {
  children: React.ReactNode
  speed?: number
  speedOnHover?: number
  direction?: 'left' | 'right'
  gap?: number
  className?: string
}

export function InfiniteSlider({
  children,
  speed = 20,
  speedOnHover = 0,
  direction = 'left',
  gap = 40,
  className = '',
}: InfiniteSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [innerWidth, setInnerWidth] = useState(0)
  const [hovered, setHovered] = useState(false)
  
  // Initial measurement
  useEffect(() => {
    const setWidths = () => {
      if (containerRef.current && innerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
        setInnerWidth(innerRef.current.offsetWidth)
      }
    }
    
    setWidths()
    // Add a small delay to ensure all images are loaded and measured correctly
    const timer = setTimeout(() => {
      setWidths();
    }, 500);
    
    window.addEventListener('resize', setWidths)
    return () => {
      window.removeEventListener('resize', setWidths);
      clearTimeout(timer);
    }
  }, [])
  
  // Animation values
  const baseX = useMotionValue(0)
  const springX = useSpring(baseX, { damping: 50, stiffness: 100 })
  
  // Update animation frame
  useAnimationFrame((time, delta) => {
    if (!innerWidth || !containerWidth) return
    
    const currentSpeed = hovered ? speedOnHover : speed
    if (currentSpeed === 0) return
    
    const directionFactor = direction === 'left' ? -1 : 1
    let moveBy = directionFactor * currentSpeed * (delta / 1000)
    
    // Calculate exact repeat distance to ensure seamless loop
    const repeat = innerWidth + gap
    
    // Ensure we wrap around smoothly
    let newPosition = baseX.get() + moveBy;
    if (direction === 'left' && newPosition < -repeat) {
      newPosition = 0;
    } else if (direction === 'right' && newPosition > repeat) {
      newPosition = 0;
    }
    
    baseX.set(newPosition);
  })
  
  const x = useTransform(springX, (value) => {
    return direction === 'left' ? value : -value
  })
  
  // Create multiple duplicates for seamless loop
  const numDuplicates = Math.max(3, Math.ceil((containerWidth * 2) / (innerWidth || 1)));
  const items = Array.isArray(children) 
    ? [...Array(numDuplicates)].flatMap(() => children) 
    : [...Array(numDuplicates)].map(() => children);
  
  return (
    <div 
      ref={containerRef}
      className={`overflow-hidden ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <motion.div 
        className="flex"
        style={{ x }}
      >
        <div 
          ref={innerRef}
          className="flex items-center"
          style={{ gap: gap + 'px' }}
        >
          {items.map((child, i) => (
            <div key={i} className="flex-shrink-0">
              {child}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  )
} 